<template>
    <UnlayerEditorProvider :on-preview-html="previewHtml" @editor="(ed) => editor = ed">
        <div v-if="isLoading" class="loading-container">
            <DsSpinner />
        </div>
        <div v-else class="unlayer-email-edit-stage">
            <UnlayerEditor
                data-qa="unlayer-editor"
                :design="emailTemplate.design"
                display-mode="email"
                :options="unlayerOptions"
                :unlayer-user-id="appId"
                :unlayer-project-id="unlayerProjectId"
                :unlayer-builder-version-override="unlayerBuilderVersionOverride"
                :use-unlayer-builder-version-override-enabled="useUnlayerBuilderVersionOverrideEnabled"
                @design-updated="handleUpdate"
                @load="handleUnlayerReady"
            />

            <portal to="unlayer-email-editor-root">
                <SaveAsTemplateModal
                    v-if="isSaveAsTemplateModalOpen"
                    :editor="editor"
                    :funnel-id="funnelId"
                    :template-id="externalTemplateId"
                    @close="handleSaveAsTemplateModalClose"
                />

                <ContentRiskModal
                    :is-open="isContentRiskModalOpen"
                    :quality="contentRiskQuality"
                    :messages="contentRiskMessages"
                    :sendable="contentRiskSendable"
                    @close="handleContentRiskModalClose"
                    @continue-to-send="handleContinueToSend"
                />

                <ContentPreviewModal
                    :preview-html="htmlToPreview"
                    @close="htmlToPreview = null"
                />

                <ShareFeedbackModal
                    :feature="shareFeedbackModalFeature"
                    :slack-channels="shareFeedbackModalSlackChannels"
                    :is-open="isFeedbackModalOpen"
                    @close="isFeedbackModalOpen = false"
                />

                <ContentRiskActions v-slot="props">
                    <SendTestEmailModal
                        :is-open="isSendTestEmailModalOpen"
                        v-bind="props"
                        :from-address="emailTemplate.from"
                        :html-body="emailTemplate.htmlBody"
                        :subject="emailTemplate.subject"
                        :preview-text="emailTemplate.previewText"
                        :funnel-id="funnelId"
                        :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                        :contact-search-enabled="contactSearchInTestEmailsEnabled"
                        @close="isSendTestEmailModalOpen = false"
                    />
                </ContentRiskActions>

                <FileUploadModal
                    v-if="isFileUploadModalOpen"
                    @file-selected="sendFileUploadResponse"
                    @close="sendFileUploadResponse"
                />

                <CreateTagModal
                    :is-open="isTagModalOpen"
                    :tag-name="tagModalTagName"
                    @tagCreated="handleCreateTag"
                    @close="cancelCreateTag"
                />
            </portal>

            <portal to="unlayer-email-editor-actions">
                <div class="header-button-row">
                    <SaveStatus
                        :status="autoSaveStatus"
                        class="template-save-status"
                        data-qa="broadcast-save-status"
                    />

                    <DsTextButton
                        class="preview-button"
                        data-qa="email-preview-button"
                        leading-icon="eye-show"
                        :loading="isLoadingPreviewSelection"
                        @click="previewHtmlFromHeader"
                    >
                        {{ $t('global.preview') }}
                    </DsTextButton>

                    <DsFilledButton
                        :loading="isSavingOnNavigateStatus || loadingContentRisk"
                        data-qa="navigate-to-send-button"
                        @click="handleCreateComplete(editor)"
                    >
                        {{ $t('global.saveAndExit') }}
                    </DsFilledButton>

                    <TemplateActionsMenu
                        v-if="editor"
                        data-qa="template-actions-menu"
                        @save-as-template="handleSaveAsTemplateModalOpen"
                        @export-html="handleExportHtml"
                        @share-feedback="isFeedbackModalOpen = true"
                        @send-test-email="handleSendTestEmail"
                        @learn-more="handleLearnMore"
                    />
                </div>
            </portal>

            <a ref="copy" data-qa="export-html-download-link-tag" @focus="$event.target.click()" />
        </div>
    </UnlayerEditorProvider>
</template>

<script>
import UnlayerEditorProvider from '@/components/unlayer/UnlayerEditorProvider';
import UnlayerEditor from '@/components/unlayer/UnlayerEditor';
import SaveAsTemplateModal from './SaveAsTemplateModal';
import TemplateActionsMenu from './TemplateActionsMenu';
import ContentRiskModal from './ContentRiskModal';
import ContentPreviewModal from './ContentPreviewModal';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import SendTestEmailModal from '@/components/unlayer/sendTestEmail/SendTestEmailModal';
import SaveStatus from '@/components/automations/SaveStatus';
import { SAVE_STATUS } from '@/components/automations/constants/automations.constants';
import { createEmailOptions, removeDismissedAuditItems, mergeCategoryWithTag } from './unlayerEmail.util';
import {
    AUTO_SAVE_INTERVAL,
    SOURCES,
    UNLAYER_BUILDER_CUSTOMIZATION_HELP_URL as HELP_URL,
customToolsUri} from '@/components/unlayer/constants/unlayer.constants';
import { EMAIL_BROADCASTS as EMAIL_BROADCASTS_FEATURE } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import { CONTENT_RISK_QUALITY, CONTENT_RISK_STATUS } from './content-risk.constants';
import FileUploadModal from './FileUploadModal';
import CreateTagModal from '@/components/tags/CreateTagModal';
import {
    getFunnelPreviewHtml,
    saveFunnelEmail,
    getBrandLogos,
    getBrandPalette,
    getUnlayerUserUploadedFiles,
    getTemplateDependencyList,
    createLogoDependency,
    deleteLogoDependency
} from '@/api/campaigns/campaignUnlayerEmail';
import { getBroadcastPreviewHtml } from '@/api/broadcasts/emailBroadcast';
import axios from 'axios';
import sentry from '@/analytics/sentry';
import clonedeep from 'lodash.clonedeep';
import ContentRiskActions from '@/components/unlayer/ContentRiskActions.vue';
import {
    cleanDependenciesFromDesign,
    updateLogoDependenciesForDuplicatedRow,
    updateLogoDependenciesForDuplicatedContent,
} from '@/components/unlayer/unlayer.util';
import {toRaw} from 'vue';

const REQUEST_TYPE_KEAP_FILE_UPLOAD = 'keapFileUploadRequest';
const REQUEST_TYPE_KEAP_CREATE_TAG = 'keapCreateTagRequest';
const REQUEST_TYPE_KEAP_GET_BRAND_LOGOS = 'getBrandLogos';
const REQUEST_TYPE_KEAP_SHOW_BRANDING_SERVICE_CENTER = 'showBrandingServiceCenter';
const REQUEST_TYPE_KEAP_GET_BRAND_LOGO_DEPENDENCIES = 'getBrandLogoDependencies';
const REQUEST_TYPE_KEAP_CREATE_LOGO_DEPENDENCY = 'createLogoDependency';
const REQUEST_TYPE_KEAP_DELETE_LOGO_DEPENDENCY = 'deleteLogoDependency';

export const UNLAYER_CONTENT_REMOVED_EVENT = 'content:removed';
export const UNLAYER_ROW_REMOVED_EVENT = 'row:removed';
export const UNLAYER_ROW_ADDED_EVENT = 'row:added';
export const UNLAYER_CONTENT_ADDED_EVENT = 'content:added';
export const REQUEST_TYPE_KEAP_GET_BRAND_COLORS = 'getBrandColors';

export default {
    components: {
        SendTestEmailModal,
        UnlayerEditorProvider,
        UnlayerEditor,
        SaveAsTemplateModal,
        SaveStatus,
        TemplateActionsMenu,
        ContentRiskModal,
        ContentPreviewModal,
        ShareFeedbackModal,
        FileUploadModal,
        CreateTagModal,
        ContentRiskActions,
    },

    inheritAttrs: false,

    isModalSendTest: false,

    props: {
        emailTemplate: Object,
        broadcastId: Number,
        appId: String,
        environment: String,
        externalTemplateId: String,
        users: Array,
        userSignatures: Object,
        unlayerProjectId: String,
        mergeFields: Array,
        campaignLinks: Array,
        bookingLinkHostname: String,
        appointmentTypes: Array,
        tags: Array,
        unlayerAppointmentToolEnabled: Boolean,
        unlayerAiToolsEnabled: Boolean,
        unlayerSmartMergeTagsEnabled: Boolean,
        unlayerBuilderVersionOverride: String,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        contactSearchInTestEmailsEnabled: Boolean,
        funnelId: String,
        loadingContentRisk: Boolean,
        contentRiskSendable: Boolean,
        contentRiskQuality: String,
        contentRiskStatus: String,
        contentRiskMessages: {
            type: Array,
            default: () => [],
        },
        fetchContentRisks: {
            type: Function,
            default: (x) => x,
        },
        source: {
            type: String,
            default: SOURCES.CAMPAIGN,
        },
        savedTemplateToCreate: Object,
        unlayerEmailLogoToolEnabled: Boolean,
        brandColors: Array,
        isMaxClassic: Boolean,
    },

    data() {
        return {
            isLoading: false,
            isSaveAsTemplateModalOpen: false,
            isContentRiskModalOpen: false,
            isFileUploadModalOpen: false,
            autoSaveStatus: SAVE_STATUS.NONE,
            saveOnNavigateStatus: SAVE_STATUS.NONE,
            autoSaveIntervalId: null,
            hasPendingUpdates: false,
            isLoadingPreviewSelection: false,
            htmlToPreview: null,
            isFeedbackModalOpen: false,
            isSendTestEmailModalOpen: false,
            fileUploadResponsePort: null,
            createTagResponsePort: null,
            isTagModalOpen: false,
            tagModalTagName: '',
            shareFeedbackModalFeature: EMAIL_BROADCASTS_FEATURE,
            shareFeedbackModalSlackChannels: [SLACK_CHANNELS.IN_APP_FEEDBACK],
            shouldCreateSavedTemplate: false,
            didSaveBeforeExit: false,
            hasDismissedAuditItems: false,
            design: null,
            html: null,
            files: [],
            brandLogos: [],
            brandPalette: [],
            logoDependencies: [],
            editor: null,
        };
    },

    async created() {
        this.autoSaveIntervalId = setInterval(this.autoSave, AUTO_SAVE_INTERVAL);
        window.addEventListener('message', this.handleUnlayerMessage);

        if (this.savedTemplateToCreate?.design) {
            this.shouldCreateSavedTemplate = true;
        }

        this.isLoading = true;
        await Promise.allSettled([
            this.fetchBrandLogos(),
            this.loadFiles(),
            this.fetchLogoDependencies(),
        ]).catch((resp) => {
            resp.forEach((res) => {
                if (res.status === 'rejected') {
                    sentry.captureException(res.reason);
                }
            })
        })


        this.isLoading = false;
    },

    beforeDestroy() {
        clearInterval(this.autoSaveIntervalId);
        window.removeEventListener('message', this.handleUnlayerMessage);

        if (!this.didSaveBeforeExit && this.design && this.html) {
            this.$emit('unsaved-changes', {
                design: this.design,
                htmlBody: this.html,
            });
        }
    },

    computed: {
        unlayerOptions() {
            return createEmailOptions({
                mergeFields: this.mergeFields,
                campaignLinks: this.campaignLinks,
                bookingLinkHostname: this.bookingLinkHostname,
                appointmentTypes: this.appointmentTypes,
                users: this.users,
                userSignatures: this.userSignatures,
                customToolsUri,
                tags: this.tags,
                files: this.files,
                environment: this.environment,
                appointmentToolEnabled: this.unlayerAppointmentToolEnabled,
                unlayerAiToolsEnabled: this.unlayerAiToolsEnabled,
                unlayerSmartMergeTagsEnabled: this.unlayerSmartMergeTagsEnabled,
                unlayerEmailBrandingCenterEnabled: this.unlayerEmailLogoToolEnabled,
                brandPalette: this.brandColors,
                isMaxClassic: this.isMaxClassic,
            });
        },

        isSavingOnNavigateStatus() {
            return this.saveOnNavigateStatus === SAVE_STATUS.SAVING;
        },

        isBroadcast() {
            return this.source === SOURCES.BROADCAST;
        },

        isCampaign() {
            return this.source === SOURCES.CAMPAIGN;
        },

        isContentRisky() {
            return this.contentRiskMessages.length > 0 && this.contentRiskQuality !== CONTENT_RISK_QUALITY.OK;
        },

        brandingCenterSettingsURL() {
            const path = `/settings/branding-center/?app_id=${this.appId}`;

            switch (this.environment.toLowerCase()) {
            case 'production':
            default:
                return `https://keap.app${path}`;
            case 'development':
            case 'sandbox':
                return `http://localhost:10239${path}`;
            case 'integration':
                return `https://integration.keap.app${path}`;
            case 'proofing':
            case 'staging':
                return `https://staging.keap.app${path}`;
            }
        },
    },

    methods: {
        async loadFiles() {
            try {
                const { files = [] } = await getUnlayerUserUploadedFiles();

                this.files = files.map(({ name, url }) => ({ value: url, label: name }));
            } catch (e) {
                // ignore
            }
        },

        handleSaveAsTemplateModalOpen() {
            this.isSaveAsTemplateModalOpen = true;
        },

        handleLearnMore() {
            window.open(HELP_URL, '_blank');
        },

        exportHtml(editor) {
            return new Promise((resolve) => {
                editor.exportHtml(
                    async (data) => {
                        this.html = data.html;
                        this.design = data.design;
                        this.$emit('update-email-template', {
                            ...this.emailTemplate,
                            design: this.design,
                            htmlBody: this.html,
                        });

                        resolve();
                    },
                    { cleanup: false },
                );
            });
        },

        async handleUnlayerReady(editor) {
            await this.exportHtml(editor);
            this.hasPendingUpdates = true;
            await this.autoSave();

            if (this.shouldCreateSavedTemplate) {
                this.shouldCreateSavedTemplate = false;
                this.createSavedTemplate();
            }
        },

        handleExportHtml() {
            const editor = toRaw(this.editor);

            editor.exportHtml(
                    (data) => {
                        const htmlBlob = new Blob([data.html]);
                        const fileUrl = window.URL.createObjectURL(htmlBlob);

                        const fileLink = this.$refs.copy;

                        fileLink.href = fileUrl;

                        const fileName = this.emailTemplate.title ?? 'Untitled Email';

                        fileLink.setAttribute('download', `${fileName}.html`);
                        fileLink.click();
                    },
                    { cleanup: false },
            );
        },

        handleSaveAsTemplateModalClose() {
            this.isSaveAsTemplateModalOpen = false;
        },

        handleContentRiskModalClose() {
            this.isContentRiskModalOpen = false;
        },

        handleContinueToSend() {
            this.handleContentRiskModalClose();

            if (this.isModalSendTest) {
                this.isSendTestEmailModalOpen = true;
                this.isModalSendTest = false;
            } else {
                this.navigateToReadyStep();
            }
        },

        async cleanDependencies() {
            const designUpdated = cleanDependenciesFromDesign(this.externalTemplateId, {
                design: this.design,
                brandLogos: this.brandLogos,
                logoDependencies: this.logoDependencies,
            });

            await this.fetchLogoDependencies();

            if (designUpdated) {
                toRaw(this.editor)?.loadDesign(this.design)
            }
        },

        async handleUpdate({ data, change }) {
            const { design, html } = data;

            this.html = html;
            this.design = design;

            if (this.hasDismissedAuditItems) {
                removeDismissedAuditItems(this.design);

                this.hasDismissedAuditItems = false;
            }

            const targetId = change?.item?.id;

            if (this.unlayerEmailLogoToolEnabled
                && targetId
                && change?.type === UNLAYER_ROW_ADDED_EVENT || UNLAYER_CONTENT_ADDED_EVENT
            ) {
                const fn = change?.type === UNLAYER_ROW_ADDED_EVENT ? updateLogoDependenciesForDuplicatedRow : updateLogoDependenciesForDuplicatedContent;

                try {
                    const resultOrPromise = fn(
                        design,
                        targetId, {
                        brandLogos: this.brandLogos,
                        logoDependencies: this.logoDependencies,
                        templateId: this.externalTemplateId,
                    }
                    );

                    if (resultOrPromise !== false) {
                        await resultOrPromise;

                        toRaw(this.editor)?.loadDesign(design);
                    }
                } catch (e) {
                    sentry.captureException(e);
                }
            }

            this.$emit('update-email-template', {
                ...this.emailTemplate,
                design,
                htmlBody: html,
            });

            this.hasPendingUpdates = true;
        },

        createSavedTemplate() {
            const template = {
                name: this.savedTemplateToCreate?.name,
                design: this.emailTemplate.design,
                html: this.emailTemplate.htmlBody,
            };

            // the axios call that's below can fail but this event
            // should still be emitted regardless of it's status.
            this.$emit('saved-template-created');

            try {
                axios.post('/app/unlayerEmailFunnel/saveAsTemplate', {
                    title: template.name,
                    html: template.html,
                    design: JSON.stringify(template.design),
                    funnelId: Number(this.funnelId),
                });
            } catch (e) {
                sentry.captureException(e);
            }
        },

        async handleUnlayerMessage(msg) {
            const { data, ports } = msg;
            const {
                keapRequestType,
                requestData,
                testPort,
                path,
                resultArgs,
            } = data;
            const [responsePort] = ports;

            const { type, item = {} } = resultArgs?.[0] || {};

            const sendPort = responsePort ?? testPort;

            const dispatchWithResponse = async (handler, errorCode = 'error.saving') => {
                try {
                    const response = await handler();

                    sendPort.postMessage({ success: true, response: clonedeep(response) });
                } catch (error) {
                    this.$error({ message: this.$t(errorCode) });
                    sendPort.postMessage({ success: false, error: `${error}` });
                }
            };

            if (path?.[0] === 'validator' && resultArgs?.[0]?.values?.audits?.length) {
                this.hasDismissedAuditItems = true;
            }

            const removeTemplateDependency = async (dependencyKey) => {
                await this.deleteLogoDependency(dependencyKey);
                if (!keapRequestType) sendPort.postMessage({ success: true });
            };

            const handleRemoveLogoTools = (contents) => {
                if (!contents) return;
                contents
                        .filter((c) => (c.type === 'custom' && c.slug === 'keap#logo_tool'))
                        .forEach((tool) => removeTemplateDependency(tool.values?.logoTool?.dependencyKey));
            };


            if (type) {
                if (type === UNLAYER_CONTENT_REMOVED_EVENT) {
                    if (item.type === 'custom' && item.slug === 'keap#logo_tool') await removeTemplateDependency(item.values?.logoTool?.dependencyKey);
                }

                if (type === UNLAYER_ROW_REMOVED_EVENT) {
                    const rowId = item.id;
                    const row = this.emailTemplate.design.body.rows.find((r) => r.id === rowId);

                    if (row) {
                        row.columns.forEach((content) => handleRemoveLogoTools(content.contents));
                    }
                }
            }

            switch (keapRequestType) {
                case REQUEST_TYPE_KEAP_FILE_UPLOAD:
                    this.isFileUploadModalOpen = true;
                    this.fileUploadResponsePort = responsePort;
                    break;

                case REQUEST_TYPE_KEAP_CREATE_TAG:
                    this.createTagResponsePort = responsePort;
                    this.openTagModal(requestData.inputValue);
                    break;

                case REQUEST_TYPE_KEAP_SHOW_BRANDING_SERVICE_CENTER:
                    // redirect to settings page
                    window.open(this.brandingCenterSettingsURL, '_blank');
                    break;

                case REQUEST_TYPE_KEAP_GET_BRAND_LOGOS:
                    return dispatchWithResponse(async () => {
                        await this.fetchBrandLogos();

                        if (requestData?.reloadDesign) {
                            await this.cleanDependencies();
                        }

                        return this.brandLogos;
                    });

                case REQUEST_TYPE_KEAP_GET_BRAND_COLORS:
                    return dispatchWithResponse(async () => {
                        await this.fetchBrandPalette();

                        return this.brandPalette;
                    });

                case REQUEST_TYPE_KEAP_GET_BRAND_LOGO_DEPENDENCIES:
                    return dispatchWithResponse(async () => {
                        await this.fetchLogoDependencies();

                        return this.logoDependencies;
                    });

                case REQUEST_TYPE_KEAP_CREATE_LOGO_DEPENDENCY:
                    return dispatchWithResponse(async () => {
                        const { logoId, isPrimary } = requestData;

                        return this.createLogoDependency(logoId, isPrimary);
                    });

                case REQUEST_TYPE_KEAP_DELETE_LOGO_DEPENDENCY:
                    return dispatchWithResponse(async () => {
                        try {
                            const {dependencyKey} = requestData;

                            await this.deleteLogoDependency(dependencyKey);

                            return true;
                        } catch (e) {
                            sentry.captureException(e);
                        }

                        return false;
                    });

                default:
                    return null;
            }

            return null;
        },

        async sendFileUploadResponse(selectedFile = {}) {
            this.isFileUploadModalOpen = false;

            if (!this.fileUploadResponsePort) {
                return;
            }

            await this.loadFiles();

            this.fileUploadResponsePort.postMessage({
                success: true,
                response: {
                    name: selectedFile.name,
                    url: selectedFile.url,
                    files: this.files,
                },
            });
        },

        async previewHtmlFromHeader() {
            this.isLoadingPreviewSelection = true;

            try {
                this.htmlToPreview = await this.getPreviewHtml();
            } catch (e) {
                this.$error({
                    message: 'Something went wrong while loading your template preview. Please try again.',
                    bottom: true,
                });
            }

            this.isLoadingPreviewSelection = false;
        },

        async previewHtml(params = {}, done = (x) => x) {
            try {
                const html = await this.getPreviewHtml();

                done({ html });
            } catch (e) {
                done({ html: params.html });
            }
        },

        async getPreviewHtml() {
            await this.autoSave();

            if (this.isBroadcast) {
                return getBroadcastPreviewHtml({ broadcastId: this.broadcastId });
            }

            return getFunnelPreviewHtml({ templateId: this.emailTemplate.id, funnelId: this.funnelId });
        },

        async handleCreateComplete(editor) {
            clearInterval(this.autoSaveIntervalId);

            await this.exportHtml(editor);

            await Promise.all([
                this.save({ auto: false }),
                this.fetchContentRisks(this.emailTemplate.htmlBody),
            ]);

            if (this.isContentRisky) {
                this.isContentRiskModalOpen = true;
            } else {
                this.didSaveBeforeExit = true;

                if (this.isCampaign) {
                    this.navigateToReadyStep();
                } else {
                    this.$emit('close');
                }
            }
        },

        async handleSendTestEmail() {
            const promises = [this.autoSave()];

            promises.push(this.fetchContentRisks(this.emailTemplate.htmlBody));

            await Promise.all(promises);

            if (this.isContentRisky) {
                this.isContentRiskModalOpen = true;
                this.isModalSendTest = true;
            } else {
                this.isSendTestEmailModalOpen = true;
            }
        },

        async autoSave() {
            try {
                if (!this.hasPendingUpdates) return;

                await this.save({ auto: true });

                this.autoSaveStatus = SAVE_STATUS.NONE;
            } catch (e) {
                sentry.captureException(e);
            }
        },

        async save({ auto }) {
            try {
                this.hasPendingUpdates = false;

                if (auto) {
                    this.autoSaveStatus = SAVE_STATUS.SAVING;
                } else {
                    this.saveOnNavigateStatus = SAVE_STATUS.SAVING;
                }

                const emailTemplate = {
                    ...this.emailTemplate,
                };

                if (this.design) {
                    emailTemplate.design = this.design;
                }

                if (this.html) {
                    emailTemplate.htmlBody = this.html;
                }

                if (this.isBroadcast) {
                    this.$emit('save', emailTemplate);
                } else {
                    const fromAddress = emailTemplate.from;

                    const emailPayload = {
                        fromAddress,

                        id: emailTemplate.id,
                        trackedLinks: emailTemplate.trackedLinks,

                        subject: emailTemplate.subject,
                        previewText: emailTemplate.previewText,

                        toAddress: emailTemplate.toAddress,

                        design: emailTemplate.design,
                        htmlBody: emailTemplate.htmlBody,
                    };

                    const { data } = await saveFunnelEmail(emailPayload, this.funnelId);

                    this.$emit('update-email-template', {
                        ...data,
                        design: JSON.parse(data.design),
                    });
                }

                if (auto) {
                    this.autoSaveStatus = SAVE_STATUS.SUCCESS;
                } else {
                    this.saveOnNavigateStatus = SAVE_STATUS.SUCCESS;
                }
            } catch (e) {
                this.hasPendingUpdates = true;

                if (auto) {
                    this.autoSaveStatus = SAVE_STATUS.FAILED;
                } else {
                    this.saveOnNavigateStatus = SAVE_STATUS.FAILED;
                }
            }
        },

        navigateToReadyStep() {
            this.$emit('template-edit-complete', this.contentRiskStatus === CONTENT_RISK_STATUS.FAILED);
        },

        openTagModal(tagName) {
            this.isTagModalOpen = true;
            this.tagModalTagName = tagName;
        },

        cancelCreateTag() {
            this.isTagModalOpen = false;

            if (!this.createTagResponsePort) return;

            this.createTagResponsePort.postMessage({
                success: false,
            });
        },

        handleCreateTag(tag) {
            this.isTagModalOpen = false;

            if (!this.createTagResponsePort) return;

            this.createTagResponsePort.postMessage({
                success: true,
                response: mergeCategoryWithTag({
                    catName: tag.category?.label,
                    tagLabel: tag.name,
                    value: `${tag.id}`,
                }),
            });
        },

        async fetchBrandLogos() {
            const { logos } = await getBrandLogos();

            this.brandLogos = [...logos];
        },

        async fetchBrandPalette() {
            const { palette } = await getBrandPalette();

            this.brandPalette = palette;
        },

        async fetchLogoDependencies() {
            try {
                const { templateDependencies } = await getTemplateDependencyList(this.externalTemplateId);

                this.logoDependencies = templateDependencies;
            } catch (e) {
                sentry.captureException(`Error loading logo dependencies in Automation. Error: ${e.message}`)
            }
        },

        async createLogoDependency(logoId, isPrimary) {
            try {
                const newDependency = await createLogoDependency(this.externalTemplateId, logoId, isPrimary);

                this.logoDependencies = [...this.logoDependencies, newDependency]

                return newDependency
            } catch (e) {
                sentry.captureException(`Error creating logo dependency in Automation. Error: ${e.message}`)
            }

            return null;
        },

        async deleteLogoDependency(dependencyKey) {
            try {
                await deleteLogoDependency(this.externalTemplateId, dependencyKey);
                const filteredDependencies = this.logoDependencies.filter((dependency) => dependency.dependencyKey !== dependencyKey);

                this.logoDependencies = [...filteredDependencies];
            } catch (e) {
                sentry.captureException(`Error deleting logo dependency in Automation. Error: ${e.message}`)
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.loading-container {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

.unlayer-email-edit-stage {
    height: 100%;
}

.header-button-row {
    display: flex;
    flex-direction: row;
    @include margin-end($spacing-100);

    button {
        @include margin-end($spacing-100);
    }
}

.template-save-status {
    align-self: center;
    @include margin-end($spacing-100);
}
</style>
